import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import aesjs from "aes-js";
import {
  assignPassword as assignPasswordAction,
  changePassword as changePasswordAction,
  tokenValidation as tokenValidationAction,
  SendUserActivationEmail,
} from "../../../actions/auth.action.entry";

import SimpleReactValidator from "simple-react-validator";
import { reactValidatorOptions } from "../../../helpers/simpleReactValidator";
import {
  hasNumbers,
  hasLowerLetter,
  hasUpperLetter,
  hasSpecialCharacter,
  isCorrectLength,
  isCorrectMaxLength,
} from "../../../helpers/regex.helper";

import { encryptionKey, encryptionIv } from "../../../config/config.entry";
import PageBackground from "../../../components/pageBackground.component";
import InputForm from "../../../components/InputForm/inputForm.component";
import ErrorBox from "../../../components/errorBox.component";
import Typography from "@material-ui/core/Typography";
import { Button, Grid, List, ListItem, styled } from "@material-ui/core";
import Check from "@material-ui/icons/Check";
import Clear from "@material-ui/icons/Clear";
import "./login.css";
import CardBody from "../../../components/CardBody/cardBody.component";
import AlertConfirm from "../../../components/alertConfirm.component";

const getMulishText = (fontSize, fontWeight) => ({
  fontFamily: "Mulish, sans-serif",
  fontSize,
  fontWeight
})

const OwnTypography = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 400,
  fontFamily: "Mulish, sans-serif",
  color: "#575756",
  lineHeight: "19.2px",
}));

const OwnButton = styled(Button)(({ theme }) => ({
  padding: "12px 20px",
  marginRight: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  width: "100%",
  height: "40px",
  display: "block",
  margin: "0",
  color: theme.palette.common.white,
  fontSize: "20px",
  fontWeight: 500,
  fontFamily: "'Mulish', sans-serif",
  textTransform: "none",
  lineHeight: "24px",
  boxShadow: "0px 4px 4px #00000033",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.primary.light,
    boxShadow: "0px 6px 8px rgba(0, 0, 0, 0.25)",
  },
  "&.MuiButton-root, & .MuiButton-root": {
    borderRadius: "8px !important",
  },
  "&.Mui-disabled": {
    backgroundColor: theme.buttonUko.buttonUkoBackgroundDisabled,
    color: theme.buttonUko.buttonUkoColorDisabled,
  },
}));

const EMPTY_FORM = {
  user: "",
  token: "",
  oldpassword: "",
  confirmpassword: "",
  newpassword: "",
};

/** Check / Clear icon used by the password requirements list */
const RuleIcon = ({ ok }) =>
  ok ? (
    <Check style={{ fontSize: 15, color: "#119f45" }} />
  ) : (
    <Clear style={{ fontSize: 15, color: "#bf0c12" }} />
  );

/** Decrypts the query string and returns the parameters object */
const parseUrlParams = () => {
  const paramsChar = decodeURIComponent(window.location.search.substring(1));

  // The cipher-block chaining mode of operation maintains internal state,
  // so to decrypt a new instance must be instantiated.
  const aesCbc = new aesjs.ModeOfOperation.cbc(encryptionKey, encryptionIv);

  const encryptedText = aesjs.utils.hex.toBytes(
    Buffer.from(paramsChar || "", "base64").toString("hex")
  );

  const decryptedBytes = aesCbc.decrypt(encryptedText);

  const decryptedText = aesjs.utils.utf8
    .fromBytes(decryptedBytes)
    .replace(/(?:\\[rn]|[\r\n]+|[\000-\031\200-\377])+/g, "");

  return JSON.parse(decryptedText);
};

const isPasswordValid = (password) =>
  isCorrectLength(password, 12) &&
  isCorrectMaxLength(password, 16) &&
  hasUpperLetter(password) &&
  hasLowerLetter(password) &&
  hasNumbers(password) &&
  hasSpecialCharacter(password);

/**
 * Change Password Component (change password sending an E-mail
 * form and make a post request for user verification).
 *
 * @export Function Component
 * @returns Redux connect
 */
export const ChangePassword = ({
  appName,
  tenant,
  history,
  authReducer,
  loadingReducer,
  assignPassword,
  changePassword,
  tokenValidation,
  menuReducer
}) => {
  // The URL parameters are decrypted only once, on the first render
  const [initialData] = useState(() => {
    try {
      const objParameters = parseUrlParams();
      return {
        ok: true,
        form: {
          user:
            history.location.state !== undefined
              ? history.location.state.user
              : objParameters.user, // comes from the query string or the request
          token: objParameters.token, // comes from the query string
          isNewUser: objParameters.isNewUser,
          oldpassword: "",
          confirmpassword: "",
          newpassword: "",
        },
      };
    } catch (error) {
      console.error(error);
      return { ok: false, form: EMPTY_FORM };
    }
  });

  const [form, setForm] = useState(initialData.form);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [showButton, setShowButton] = useState(true); // true = button disabled
  const [getErrors, setGetErrors] = useState("");

  // Replaces this.forceUpdate() so the validator can show its messages
  const [, forceUpdate] = useReducer((n) => n + 1, 0);

  // Lazy initial state: the validator is created only once
  const [validator] = useState(
    () => new SimpleReactValidator(reactValidatorOptions)
  );

  // Derived directly from props, no need to copy them into local state
  const loading = loadingReducer.loading;
  const authResponse = authReducer.getAuthenticationResponse;
  const invalidToken = authReducer.isInvalidToken;

  const cleanChangeForm = useCallback(() => {
    setForm({ ...EMPTY_FORM });
    validator.hideMessages();
  }, [validator]);

  // The link is not valid: redirect (done here, not while rendering)
  useEffect(() => {
    if (!initialData.ok) {
      history.push({
        pathname: "/Alert",
        state: {
          message: "El enlace no es válido.",
          pagename: "/Login",
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mount: validates the token if there is one
  useEffect(() => {
    if (form.token && !authReducer.isLoginAutomatic) {
      tokenValidation(form.user, form.token, appName, history);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Invalid token: go to the "resend e-mail" page
  const prevInvalidToken = useRef(invalidToken);
  useEffect(() => {
    if (prevInvalidToken.current === invalidToken) return;
    prevInvalidToken.current = invalidToken;
    if (!invalidToken) return;

    history.push({
      pathname: "/resendemail",
      state: {
        message: invalidToken.message,
        userData: {
          appname: appName,
          username: form.user,
        },
      },
    });
  }, [invalidToken, history, appName, form.user]);

  // Reacts to a new response of the change / assign password request
  const prevAuthResponse = useRef(authResponse);
  useEffect(() => {
    if (prevAuthResponse.current === authResponse) return;
    prevAuthResponse.current = authResponse;
    if (!authResponse) return;

    if (authResponse.responseCode === 201) {
      setIsAlertOpen(true);
    } else {
      setGetErrors(authResponse.message);
    }
  }, [authResponse, cleanChangeForm]);

  const syncChanges = (e) => {
    const id = e.target.id ? e.target.id : e.target.name;
    const value = e.target.value;

    const updatedForm = { ...form, [id]: value };
    const password = updatedForm.newpassword;
    const confirmPassword = updatedForm.confirmpassword;

    setForm(updatedForm);
    setGetErrors("");
    setShowButton(
      !password?.trim() || !confirmPassword?.trim() || !isPasswordValid(password)
    );
  };

  const comparePasswords = () => {
    if (form.newpassword !== form.confirmpassword) {
      setGetErrors("Las contraseñas no coinciden.");
      return false;
    }
    if (isPasswordValid(form.newpassword)) {
      return true;
    }
    setGetErrors(
      "La contraseña no cumple con el nivel mínimo de seguridad requerida."
    );
    return false;
  };

  const setChangePassword = (e) => {
    e.preventDefault();

    if (validator.allValid()) {
      // Compare both passwords
      if (comparePasswords()) {
        const { user, newpassword, token, oldpassword, isNewUser } = form;

        // If there is no token the "change" method is used, otherwise "assign"
        if (token === "" || token === undefined) {
          changePassword(
            {
              user: user,
              password: oldpassword,
              newpassword: newpassword,
              appname: appName,
            },
            history
          );
        } else {
          assignPassword(
            {
              user: user,
              password: newpassword,
              token: token,
              appname: appName,
            },
            null,
            tenant,
            isNewUser
          );
        }
      }
    } else {
      validator.showMessages();
      forceUpdate();
    }
  };

  const title = form.isNewUser ? "Activar cuenta" : "Cambiar contraseña";

  return (
    <div className="login">
      <PageBackground></PageBackground>
      <CardBody
        cardStyles={{
          minWidth: "370px",
          alignItems: "center",
          minHeight: "550px",
        }}
        title={
          <span
            style={{
              whiteSpace: "nowrap",
              textAlign: "center",
              width: "100%",
              margin: "0 auto",
              fontSize: "24px",
            }}
          >
            {title}
          </span>
        }
      >
        <form id="change-pass" onSubmit={setChangePassword} autoComplete="off">
          <hr></hr>
          <div style={{ width: "100%", wordBreak: "break-word" }}>
            <Typography
              style={{
                marginTop: "25px",
                marginBottom: "-2px",
                whiteSpace: "normal", // por si hay restricción previa
                lineHeight: "1.5",
              }}
            >
              Usuario: {form.user}
            </Typography>
          </div>
          <br />
          <InputForm
            placeholder={"Nueva Contraseña*"}
            icon={"Lock"}
            type={"password"}
            id="newpassword"
            onChange={syncChanges}
            value={form.newpassword}
            validator={validator}
            validateOptions={"required"}
          />
          <br />
          <InputForm
            placeholder={"Confirmar Contraseña*"}
            icon={"Lock"}
            type={"password"}
            id="confirmpassword"
            onChange={syncChanges}
            value={form.confirmpassword}
            validator={validator}
            validateOptions={"required"}
          />
          <hr style={{ marginTop: "24px" }} />
          <Grid>
            <Typography
              style={{
                marginTop: "20px",
                fontSize: 12,
                lineHeight: "19.2px",
                fontWeight: 400,
                fontFamily: "Mulish, sans-serif",
                color: "#575756",
              }}
            >
              Tu contraseña debe tener como mínimo:
            </Typography>
            <List dense={true}>
              <ListItem>
                <RuleIcon ok={hasUpperLetter(form.newpassword)} />
                <OwnTypography> Una mayúscula.</OwnTypography>
              </ListItem>
              <ListItem>
                <RuleIcon ok={hasLowerLetter(form.newpassword)} />
                <OwnTypography>Una minúscula.</OwnTypography>
              </ListItem>
              <ListItem>
                <RuleIcon ok={hasNumbers(form.newpassword)} />
                <OwnTypography>Un número.</OwnTypography>
              </ListItem>
              <ListItem>
                <div style={{ display: "flex" }}>
                  <RuleIcon ok={hasSpecialCharacter(form.newpassword)} />
                  <div style={{ marginTop: "-2px" }}>
                    <OwnTypography> Un carácter especial </OwnTypography>
                  </div>
                </div>
              </ListItem>
              <ListItem>
                <RuleIcon
                  ok={
                    isCorrectLength(form.newpassword, 12) &&
                    isCorrectMaxLength(form.newpassword, 16)
                  }
                />
                <OwnTypography>Entre 12 y 16 caracteres.</OwnTypography>
              </ListItem>
            </List>
            {getErrors && <ErrorBox value={getErrors}></ErrorBox>}
          </Grid>
          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "10px",
              padding: 0,
            }}
          >
            <div style={{ width: "100%" }}>
              <OwnButton type="submit" loading={loading} disabled={showButton}>
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: 510,
                    color: "inherit",
                    textTransform: "none",
                    letterSpacing: "0.5px",
                  }}
                >
                  {title}
                </Typography>
              </OwnButton>
            </div>
          </Grid>
        </form>
      </CardBody>
      {isAlertOpen && (
        <AlertConfirm
          isOpen={isAlertOpen}
          modalMessage={form.isNewUser ? "¡Tu cuenta ha sido activada!" :"¡Contraseña actualizada!"}
          stylesModalMessage={{
            ...getMulishText(20, 800),
            color: '#575756'
          }}
          handleAccept={
            () => {
              history?.push("/Login");
            }
          }
          showBtnAccept
          textButtonAccept={"Aceptar"}
          stylesButtonAccept={{
            width: 124,
            height: 45,
          }}
          stylesLabelButtonAccept={getMulishText(20, 700)}
        />
        )
      }
    </div>
  );
};

ChangePassword.propTypes = {
  assignPassword: PropTypes.func.isRequired,
  authReducer: PropTypes.object.isRequired,
  loadingReducer: PropTypes.object.isRequired,
  changePassword: PropTypes.func.isRequired,
  tokenValidation: PropTypes.func.isRequired,
  SendUserActivationEmail: PropTypes.func.isRequired,
  tenant: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  authReducer: state.authReducer,
  loadingReducer: state.loadingReducer,
  menuReducer: state.menuReducer.getMenuResponse,
});

export default connect(mapStateToProps, {
  assignPassword: assignPasswordAction,
  changePassword: changePasswordAction,
  tokenValidation: tokenValidationAction,
  SendUserActivationEmail,
})(ChangePassword);